import React, { useState, useEffect } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { type StripeCardElementOptions, type PaymentMethod } from '@stripe/stripe-js'
import Dlg from './Dlg'
import './AddOrUpdateCardDlg.css'

import { Box, Backdrop, CircularProgress } from '@mui/material'
import { type MouseEventHandler } from 'react'
import { usePutMethodMutation } from '../redux/api/paymentApi'
import { useDispatch, useSelector } from 'react-redux'
import { closeCardDlg, setCardErrorMessage } from '../redux/features/cardDlgSlice'
import { clearSetupIntent } from '../redux/features/intentSlice'

interface CardElementEvent {
  complete: boolean
  brand: string
  elementType: string
  empty: boolean
  error: { code: string, message: string, type: string } | null | undefined
  value: { postalCode: string }
}

const CARD_OPTIONS: StripeCardElementOptions = {
  iconStyle: 'default',
  style: {
    base: {
      iconColor: '#ADB8CC',
      color: '#7D8FB3',
      fontWeight: 700,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '14px',
      '::placeholder': {
        color: '#7D8FB3'
      }
    },
    invalid: {
      iconColor: '#ADB8CC',
      color: 'rgba(230, 46, 46, 1)'
    }
  },
  classes: {
    base: 'stripe-input-base',
    invalid: 'stripe-input-invalid'
  }
}

interface UpdateCardDlgProps {
  handleClose: MouseEventHandler<HTMLButtonElement>
  setupIntentSecret: string
  isAddCardDlg: boolean
}

const AddOrUpdateCardDlg: React.FC<UpdateCardDlgProps> = ({ handleClose, setupIntentSecret, isAddCardDlg }) => {
  const [stripePaymentMethodId, setStripePaymentMethodId] = useState<string | PaymentMethod>('')
  const [putMethod, putMethodResult] = usePutMethodMutation()
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const dlg = useSelector((state: any) => state.cardDlgState)
  const [formComplete, setFormComplete] = useState(false)

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault()
    const card = elements?.getElement(CardElement)

    if (!stripe || !elements || !card) {
      return
    }

    let methodId = stripePaymentMethodId
    if (!stripePaymentMethodId) {
      const { setupIntent, error } = await stripe.confirmCardSetup(setupIntentSecret, {
        payment_method: {
          card
        }
      })
      if ((error ?? !setupIntent) || !setupIntent.payment_method || setupIntent.status !== 'succeeded') {
        const err: string = error?.message
          ? error.message.toString()
          : "Can't confirm Stripe setupIntent!"
        dispatch(setCardErrorMessage(err))
        setFormComplete(false)
        return
      }
      if (setupIntent && setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        setStripePaymentMethodId(setupIntent.payment_method)
        methodId = setupIntent.payment_method
      }
    }

    if (methodId) {
      const cookie = document.cookie.match(/XSRF-TOKEN=([^%]+)/si)
      const xsrf: string = cookie !== null ? cookie[1].concat('=') : 'err'
      void putMethod({ paymentMethodId: methodId, token: xsrf })
        .then(() => {})
    } else {
      dispatch(setCardErrorMessage('Adding a payment method is not available.'))
      setFormComplete(false)
      return
    }
    setFormComplete(true)
  }

  useEffect(() => {
    if (putMethodResult.isSuccess && formComplete) {
      setFormComplete(false)
      dispatch(closeCardDlg())
      dispatch(clearSetupIntent())
    }

    if (putMethodResult.isError) {
      let msg = ''
      if ('status' in putMethodResult.error) {
        const errorData = putMethodResult.error.data
        if ('message' in errorData) {
          msg = errorData.message
        }
      } else {
        msg = putMethodResult.error.message ?? ''
      }
      const err = msg
        ? 'Adding a payment method is not available. Error message: ' + msg
        : 'Adding a payment method is not available.'
      dispatch(setCardErrorMessage(err))
      setFormComplete(false)
    }
  }, [putMethodResult, formComplete])

  const validateCardElement = (event: CardElementEvent) => {
    setFormComplete(event.complete)
    dispatch(setCardErrorMessage(event.error?.message))
  }

  return (
    <>
    <Dlg
      name={`${isAddCardDlg ? 'Add Card Details' : 'Update Card Details'}`}
      open={dlg.openCardDialog}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      okDisabled={formComplete}
      errorMessage={dlg.cardErrorMessage}
    >
      <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={formComplete && putMethodResult.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ py: 3 }}>
        <CardElement
          options={CARD_OPTIONS}
          onChange={validateCardElement}
        />
      </Box>
      </>
    </Dlg>
  </>
  )
}

export default AddOrUpdateCardDlg
